import { useEffect, useState, useRef } from 'react';
import { SkeletonList } from '../../components/Skeleton';
import { collection, serverTimestamp, addDoc, getDocs } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { usePreviewNavigate } from '../../contexts/PreviewContext';
import { useSupplierAccess, useSupplierViews, mergeViews } from '../../hooks/useSupplierPortal';
import { useT } from '../../contexts/LocaleContext';
import { LanguageSwitch } from '../../components/LanguageSwitch';
import SupplierCalendar from './SupplierCalendar';

const VIEW_KEY = 'mst_supplier_tours_view';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

const DOC_TYPES = ['insurance', 'health_safety', 'hygiene_cert', 'pool_maintenance', 'fire_escape', 'other'];

function TourStatusBadge({ status, itineraryPending }) {
  const t = useT();
  if (itineraryPending) {
    return (
      <span className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold bg-[#FFF7ED] text-[#92400E] whitespace-nowrap flex-shrink-0">
        <span className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#F59E0B]" />
        {t('supplier.badge.itinerary')}
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold bg-[#FFF7ED] text-[#92400E] whitespace-nowrap flex-shrink-0">
        <span className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#F59E0B]" />
        {t('supplier.badge.pending')}
      </span>
    );
  }
  if (status === 'confirmed') {
    return (
      <span className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold bg-[#ECFDF5] text-[#065F46] whitespace-nowrap flex-shrink-0">
        <span className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#10B981]" />
        {t('supplier.badge.confirmed')}
      </span>
    );
  }
  if (status === 'declined') {
    return (
      <span className="inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold bg-[#FEE2E2] text-[#991B1B] whitespace-nowrap flex-shrink-0">
        <span className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#C0392B]" />
        {t('supplier.badge.declined')}
      </span>
    );
  }
  return null;
}

function ComplianceDocsSection({ supplierId }) {
  const t = useT();
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [docName, setDocName] = useState('');
  const [docType, setDocType] = useState('insurance');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!supplierId) return;
    getDocs(collection(db, 'suppliers', supplierId, 'documents'))
      .then((snap) => {
        setDocs(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0))
        );
      })
      .catch(console.error)
      .finally(() => setLoadingDocs(false));
  }, [supplierId]);

  async function handleUpload(e) {
    e.preventDefault();
    if (!file || !docName.trim()) return;
    setUploadError('');
    setUploading(true);
    try {
      const path = `supplierDocs/${supplierId}/${Date.now()}_${file.name}`;
      const fileRef2 = storageRef(storage, path);
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(fileRef2, file);
        task.on('state_changed',
          (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve
        );
      });
      const url = await getDownloadURL(fileRef2);
      const newDoc = {
        name: docName.trim(),
        type: docType,
        url,
        fileName: file.name,
        uploadedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'suppliers', supplierId, 'documents'), newDoc);
      setDocs((prev) => [{ id: ref.id, ...newDoc, uploadedAt: { seconds: Date.now() / 1000 } }, ...prev]);
      setDocName('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setShowUpload(false);
    } catch (err) {
      console.error(err);
      setUploadError(t('supplier.docs.failed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }

  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[14.5px] font-bold text-[#111827]">{t('supplier.docs.title')}</h3>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">{t('supplier.docs.desc')}</p>
        </div>
        <button
          onClick={() => setShowUpload((v) => !v)}
          className="flex items-center gap-1.5 text-[12.5px] font-bold text-pb hover:text-navy transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('supplier.docs.upload')}
        </button>
      </div>

      {showUpload && (
        <form onSubmit={handleUpload} className="bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.docs.name')}</label>
              <input
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder={t('supplier.docs.namePh')}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
              />
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.docs.type')}</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
              >
                {DOC_TYPES.map((key) => (
                  <option key={key} value={key}>{t(`supplier.docs.types.${key}`)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.docs.file')}</label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="w-full text-[13px] text-[#374151] file:mr-3 file:py-1.5 file:px-3 file:rounded-[7px] file:border-0 file:text-[12px] file:font-semibold file:bg-pb file:text-white hover:file:bg-navy"
            />
          </div>
          {uploadError && <p className="text-[12px] text-dr">{uploadError}</p>}
          {uploading && (
            <div className="w-full bg-[#E5E7EB] rounded-full h-1.5">
              <div className="bg-pb h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={uploading || !file || !docName.trim()}
              className="px-[16px] py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60"
            >
              {uploading ? t('supplier.docs.uploading', { n: uploadProgress }) : t('supplier.docs.uploadBtn')}
            </button>
            <button
              type="button"
              onClick={() => setShowUpload(false)}
              className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] transition"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {loadingDocs ? (
        <p className="text-[12px] text-[#9CA3AF]">{t('supplier.loading')}</p>
      ) : docs.length === 0 ? (
        <p className="text-[12px] text-[#9CA3AF]">{t('supplier.docs.none')}</p>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const typeLabel = DOC_TYPES.includes(d.type) ? t(`supplier.docs.types.${d.type}`) : d.type;
            const uploadDate = d.uploadedAt?.toDate
              ? d.uploadedAt.toDate().toLocaleDateString(t.dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })
              : d.uploadedAt?.seconds
              ? new Date(d.uploadedAt.seconds * 1000).toLocaleDateString(t.dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })
              : '';
            return (
              <div key={d.id} className="flex items-center justify-between bg-[#F9FAFB] rounded-[9px] border border-[#E5E7EB] px-4 py-[10px]">
                <div>
                  <p className="text-[13px] font-semibold text-[#111827]">{d.name}</p>
                  <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{typeLabel}{uploadDate && ` · ${uploadDate}`}</p>
                </div>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12.5px] font-bold text-pb hover:text-navy transition whitespace-nowrap ml-4"
                >
                  {t('supplier.docs.view')}
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SupplierDashboard() {
  const t = useT();
  const { currentUser, logout } = useAuth();
  const navigate = usePreviewNavigate();
  const access = useSupplierAccess();
  const { loading, views } = useSupplierViews();
  const [showPast, setShowPast] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'calendar' ? 'calendar' : 'list'; } catch { return 'list'; }
  });
  function chooseView(v) {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* not remembered */ }
  }

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  // One card per tour: tours where they're on an activity, the hotel, or booked.
  const byTour = {};
  for (const v of views) (byTour[v.requestId] = byTour[v.requestId] || []).push(v);
  const today = new Date().toISOString().slice(0, 10);
  const tours = Object.entries(byTour)
    .map(([requestId, list]) => ({ requestId, ...mergeViews(list) }))
    // Upcoming first (soonest at the top), then past tours, most recent first.
    .sort((a, b) => {
      const pa = (a.tour?.returnDate || '') < today;
      const pb = (b.tour?.returnDate || '') < today;
      if (pa !== pb) return pa ? 1 : -1;
      const da = a.tour?.departureDate || '';
      const dbb = b.tour?.departureDate || '';
      return pa ? dbb.localeCompare(da) : da.localeCompare(dbb);
    });

  // Current and upcoming tours by default; finished ones on request.
  const isPast = (x) => !!x.tour?.returnDate && x.tour.returnDate < today;
  const pastCount = tours.filter(isPast).length;
  const shownTours = showPast ? tours : tours.filter((x) => !isPast(x));

  const totalPending = tours.reduce((n, x) =>
    n + (x.bookings || []).filter((b) => b.status === 'pending').length + (x.approvals || []).length, 0);

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  return (
    <div className="min-h-screen bg-page flex flex-col">

      {/* ── Topbar ── */}
      <header className="h-14 bg-navy flex items-center justify-between px-4 flex-shrink-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[8px] bg-ba flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[12px] tracking-wide">MST</span>
          </div>
          <span className="hidden sm:inline text-white font-semibold text-[15px]">{t('supplier.portal')}</span>
          <span className="hidden min-[400px]:inline bg-white/15 text-white text-[11px] px-3 py-0.5 rounded-[20px]">{t('supplier.role')}</span>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitch />
          {totalPending > 0 && (
            <span className="bg-ba text-white text-[11px] font-bold px-2.5 py-0.5 rounded-[20px]">
              {t('supplier.pendingN', { n: totalPending })}
            </span>
          )}
          <div className="flex items-center gap-2 bg-white/10 rounded-[20px] px-3 py-1.5">
            <span className="w-6 h-6 rounded-full bg-bt flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
              {initials}
            </span>
            <span className="hidden sm:inline text-white text-[13px] max-w-[140px] truncate">{displayName}</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-white/70 hover:text-white text-[12px] transition px-1"
          >
            {t('supplier.signOut')}
          </button>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="flex-1 px-4 py-5 md:px-8 md:py-8 overflow-y-auto max-w-3xl mx-auto w-full page-fade">
        {loading ? (
          <SkeletonList count={3} />
        ) : (
          <div className="space-y-8">
            {/* Tours section */}
            <div>
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <h2 className="text-[20px] font-bold text-[#111827]">{t('supplier.yourTours')}</h2>
                  <p className="text-[13px] text-[#6B7280] mt-0.5">
                    {shownTours.length === 0
                      ? (pastCount ? t('supplier.noCurrent') : t('supplier.noTours'))
                      : t(`supplier.${showPast ? 'countAll' : 'countUpcoming'}${shownTours.length === 1 ? 'One' : 'Many'}`, { n: shownTours.length })}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {tours.length > 0 && (
                    <div data-preview-allow className="inline-flex bg-[#F3F4F6] rounded-[9px] p-[3px]">
                      {['list', 'calendar'].map((v) => (
                        <button
                          key={v}
                          onClick={() => chooseView(v)}
                          className={`px-3 py-1 text-[12.5px] font-semibold rounded-[7px] transition ${view === v ? 'bg-white text-[#111827] shadow-[0_1px_2px_rgba(16,24,40,.08)]' : 'text-[#6B7280] hover:text-[#374151]'}`}
                        >
                          {t(`supplier.calendar.${v}View`)}
                        </button>
                      ))}
                    </div>
                  )}
                  {pastCount > 0 && view === 'list' && (
                    <label data-preview-allow className="flex items-center gap-2 text-[12.5px] text-[#374151] cursor-pointer whitespace-nowrap">
                      <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
                      {t('supplier.showPast', { n: pastCount })}
                    </label>
                  )}
                </div>
              </div>

              {view === 'calendar' && tours.length > 0 ? (
                <SupplierCalendar tours={tours} onOpen={(id) => navigate(`/supplier/tour/${id}`)} />
              ) : shownTours.length === 0 ? (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-10 text-center">
                  <p className="text-[#9CA3AF] text-[13px]">{pastCount ? t('supplier.nothingComing') : t('supplier.noToursShort')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {shownTours.map((tour) => {
                    const bookings = tour.bookings || [];
                    const itineraryPending = (tour.approvals || []).length > 0;
                    let effectiveStatus = null;
                    if (bookings.some((b) => b.status === 'pending')) effectiveStatus = 'pending';
                    else if (bookings.some((b) => b.status === 'declined')) effectiveStatus = 'declined';
                    else if (bookings.length) effectiveStatus = 'confirmed';

                    const serviceTypes = [...new Set([
                      ...bookings.flatMap((b) => (Array.isArray(b.serviceTypes) && b.serviceTypes.length ? b.serviceTypes : (b.serviceType ? [b.serviceType] : []))),
                      ...(tour.myItems || []).map((i) => i.activityType).filter(Boolean),
                    ])];
                    const activityCount = (tour.myItems || []).length;

                    return (
                      <button
                        key={tour.requestId}
                        data-preview-nav
                        onClick={() => navigate(`/supplier/tour/${tour.requestId}`)}
                        className="w-full text-left bg-white rounded-[12px] border border-[#E5E7EB] hover:border-[#9CA3AF] hover:shadow-[0_2px_8px_rgba(0,0,0,.06)] transition-all p-5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[14px] text-[#111827]">
                              {tour.tour?.tourName || `${t.value('sport', tour.tour?.sport) || ''} — ${tour.tour?.destination || ''}`}
                            </p>
                            <p className="text-[13px] text-[#6B7280] mt-0.5">
                              {formatDate(tour.tour?.departureDate)} → {formatDate(tour.tour?.returnDate)}
                              {activityCount > 0 && ` · ${activityCount === 1 ? t('supplier.activityOne') : t('supplier.activityMany', { n: activityCount })}`}
                            </p>
                            {serviceTypes.length > 0 && (
                              <div className="flex flex-wrap gap-[5px] mt-2">
                                {serviceTypes.map((type) => (
                                  <span key={type} className="text-[11px] font-semibold bg-[#F3F4F6] text-[#374151] px-[8px] py-[3px] rounded-[20px]">
                                    {t.value('activity', type)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <TourStatusBadge status={effectiveStatus} itineraryPending={itineraryPending} />
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Compliance docs */}
            {access?.supplierIds?.length > 0 && (
              <ComplianceDocsSection supplierId={access.supplierIds[0]} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
